import { useEffect, useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { Link, useParams } from "react-router";
import type {
  RuntimeFlagResolutionSource,
  RuntimeFlagScope,
  RuntimeFlagTarget,
  RuntimeFlagType,
  RuntimeFlagValue,
} from "@runcheetah/shared";

import { CodeText, InfoTile, Select, Textarea } from "@/components/common";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import {
  RUNTIME_FLAG_ROOT_PATH,
  getRuntimeFlagGroupBySlug,
  getRuntimeFlagGroupsForSlug,
  groupRuntimeFlags,
  type GroupedRuntimeFlags,
} from "@/lib/runtime-flag-groups";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type RuntimeFlag = RouterOutputs["admin"]["runtimeFlags"]["list"][number];
type Brand = RouterOutputs["admin"]["brands"]["list"][number];

type RuntimeFlagScopeMode = RuntimeFlagScope;

export const stringifyRuntimeFlagValue = (value: RuntimeFlagValue): string => {
  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return JSON.stringify(value, null, 2);
};

export const parseRuntimeFlagInputValue = (
  flag: { type: RuntimeFlagType },
  value: string | boolean,
): RuntimeFlagValue => {
  if (flag.type === "boolean") {
    return value === true || value === "true";
  }

  if (flag.type === "number") {
    return Number(value);
  }

  if (flag.type === "json") {
    return JSON.parse(String(value)) as RuntimeFlagValue;
  }

  return String(value);
};

export const buildRuntimeFlagTarget = (
  scope: RuntimeFlagScopeMode,
  brandId: string,
): RuntimeFlagTarget => {
  if (scope === "brand") {
    return { scope: "brand", scopeId: brandId };
  }

  return { scope: "global" };
};

export const getRuntimeFlagSourceLabel = (source: RuntimeFlagResolutionSource): string => {
  if (source === "brand") {
    return "Brand override";
  }

  if (source === "global") {
    return "Global override";
  }

  return "Default";
};

export const getRuntimeFlagEnumOptionLabel = (flagName: string, option: string): string => {
  if (flagName === "brand_onboarding_mode") {
    if (option === "standard") {
      return "Standard";
    }

    if (option === "chatbot") {
      return "Chatbot";
    }
  }

  return option;
};

export const isRuntimeFlagOverriddenForTarget = (
  flag: Pick<RuntimeFlag, "source">,
  target: RuntimeFlagTarget,
): boolean => flag.source === target.scope;

export const getRuntimeFlagTargetScopeLabel = (
  flag: Pick<RuntimeFlag, "allowedScopes">,
  target: RuntimeFlagTarget,
): string | null => {
  if (flag.allowedScopes.includes(target.scope)) {
    return null;
  }

  if (flag.allowedScopes.length === 1 && flag.allowedScopes[0] === "global") {
    return "Global only";
  }

  if (flag.allowedScopes.length === 1 && flag.allowedScopes[0] === "brand") {
    return "Brand only";
  }

  return "Unavailable for selected scope";
};

const RuntimeFlagEditor = ({ flag, target }: { flag: RuntimeFlag; target: RuntimeFlagTarget }) => {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState(stringifyRuntimeFlagValue(flag.value));
  const [clientError, setClientError] = useState<string | null>(null);

  const update = trpc.admin.runtimeFlags.update.useMutation({
    onSuccess: async () => {
      setClientError(null);
      await utils.admin.runtimeFlags.list.invalidate({ target });
    },
  });

  const reset = trpc.admin.runtimeFlags.reset.useMutation({
    onSuccess: async () => {
      await utils.admin.runtimeFlags.list.invalidate({ target });
    },
  });

  const isPending = update.isPending || reset.isPending;
  const value = stringifyRuntimeFlagValue(flag.value);
  const isDirty = draft !== value;
  const defaultValue = stringifyRuntimeFlagValue(flag.defaultValue);
  const errorMessage = clientError ?? update.error?.message ?? reset.error?.message ?? null;
  const isAllowedForTarget = flag.allowedScopes.includes(target.scope);
  const isOverriddenForTarget = isRuntimeFlagOverriddenForTarget(flag, target);
  const sourceLabel = getRuntimeFlagSourceLabel(flag.source);
  const targetScopeLabel = getRuntimeFlagTargetScopeLabel(flag, target);

  useEffect(() => {
    setDraft(value);
    setClientError(null);
  }, [value]);

  const save = () => {
    try {
      setClientError(null);
      update.mutate({
        name: flag.name,
        value: parseRuntimeFlagInputValue(flag, draft),
        target,
      });
    } catch (error) {
      setClientError(error instanceof Error ? error.message : "Invalid runtime flag value.");
    }
  };

  return (
    <Card className={flag.type === "json" ? "xl:col-span-2" : undefined}>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{flag.displayName}</CardTitle>
            <CodeText className="mt-1 block">{flag.name}</CodeText>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={flag.isOverridden ? "default" : "outline"}>{sourceLabel}</Badge>
            {targetScopeLabel ? <Badge variant="outline">{targetScopeLabel}</Badge> : null}
            <Badge variant="secondary">{flag.type}</Badge>
          </div>
        </div>
        {flag.description ? (
          <p className="text-sm text-muted-foreground">{flag.description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 md:grid-cols-2">
          <InfoTile
            label="Effective value"
            value={value}
            className="rounded-none border-0 bg-transparent p-0"
            valueClassName="whitespace-pre-wrap font-mono"
          />
          <InfoTile
            label="Fallback default"
            value={defaultValue}
            className="rounded-none border-0 bg-transparent p-0"
            valueClassName="whitespace-pre-wrap font-mono"
          />
        </div>

        {!isAllowedForTarget ? (
          <p className="text-sm text-muted-foreground">
            This flag is only configurable globally. Switch back to Global scope to edit it.
          </p>
        ) : null}

        {flag.type === "enum" ? (
          <Select
            className="h-10"
            value={draft}
            onChange={(event) => {
              setClientError(null);
              setDraft(event.target.value);
            }}
            disabled={isPending || !isAllowedForTarget}
          >
            {(flag.enumValues ?? []).map((option) => (
              <option key={option} value={option}>
                {getRuntimeFlagEnumOptionLabel(flag.name, option)}
              </option>
            ))}
          </Select>
        ) : flag.type === "boolean" ? (
          <Button
            variant="outline"
            disabled={isPending || !isAllowedForTarget}
            onClick={() => {
              setClientError(null);
              setDraft(draft === "true" ? "false" : "true");
            }}
          >
            {draft === "true" ? "Enabled" : "Disabled"}
          </Button>
        ) : flag.type === "json" ? (
          <Textarea
            value={draft}
            disabled={isPending || !isAllowedForTarget}
            className="min-h-48 font-mono text-xs"
            spellCheck={false}
            onChange={(event) => {
              setClientError(null);
              setDraft(event.target.value);
            }}
          />
        ) : (
          <Input
            type={flag.type === "number" ? "number" : "text"}
            value={draft}
            disabled={isPending || !isAllowedForTarget}
            onChange={(event) => {
              setClientError(null);
              setDraft(event.target.value);
            }}
          />
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={save} disabled={!isDirty || isPending || !isAllowedForTarget}>
            {update.isPending ? "Saving..." : "Save override"}
          </Button>
          <Button
            variant="outline"
            onClick={() => reset.mutate({ name: flag.name, target })}
            disabled={!isOverriddenForTarget || isPending || !isAllowedForTarget}
          >
            {reset.isPending ? "Resetting..." : "Reset selected scope"}
          </Button>
          {errorMessage ? <span className="text-sm text-destructive">{errorMessage}</span> : null}
        </div>
      </CardContent>
    </Card>
  );
};

const RuntimeFlagScopeSelector = ({
  brands,
  brandId,
  scope,
  onBrandChange,
  onScopeChange,
}: {
  brands: Brand[];
  brandId: string;
  scope: RuntimeFlagScopeMode;
  onBrandChange: (brandId: string) => void;
  onScopeChange: (scope: RuntimeFlagScopeMode) => void;
}) => (
  <Card>
    <CardContent className="grid gap-4 py-4 md:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
      <label className="space-y-2">
        <span className="text-sm font-medium">Scope</span>
        <Select
          value={scope}
          onChange={(event) => onScopeChange(event.target.value as RuntimeFlagScopeMode)}
        >
          <option value="global">Global</option>
          <option value="brand">Brand</option>
        </Select>
      </label>
      <label className="space-y-2">
        <span className="text-sm font-medium">Brand</span>
        <Select
          value={brandId}
          disabled={scope !== "brand" || brands.length === 0}
          onChange={(event) => onBrandChange(event.target.value)}
        >
          {brands.length === 0 ? <option value="">No brands available</option> : null}
          {brands.map((brand) => (
            <option key={brand.id} value={brand.id}>
              {brand.sourceDomain} ({brand.id})
            </option>
          ))}
        </Select>
      </label>
    </CardContent>
  </Card>
);

const RuntimeFlagGroupSection = ({
  group,
  target,
}: {
  group: GroupedRuntimeFlags<RuntimeFlag>;
  target: RuntimeFlagTarget;
}) => (
  <section className="space-y-4 rounded-2xl border bg-muted/20 p-4">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 space-y-1">
        <h2 className="text-lg font-semibold tracking-tight">{group.title}</h2>
        <p className="max-w-3xl text-sm text-muted-foreground">{group.description}</p>
      </div>
      <Badge variant="outline">
        {group.flags.length} {group.flags.length === 1 ? "flag" : "flags"}
      </Badge>
    </div>
    <div className="grid gap-4 xl:grid-cols-2">
      {group.flags.map((flag) => (
        <RuntimeFlagEditor key={flag.name} flag={flag} target={target} />
      ))}
    </div>
  </section>
);

const UnknownRuntimeFlagGroup = () => (
  <div className="space-y-6">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Runtime Flags</h1>
      <p className="text-sm text-muted-foreground">
        The selected runtime flag category does not exist.
      </p>
    </div>
    <Card>
      <CardContent className="space-y-4 py-8 text-center">
        <p className="text-sm text-muted-foreground">
          Choose a category from the sidebar or return to the full runtime flag list.
        </p>
        <Button asChild variant="outline">
          <Link to={RUNTIME_FLAG_ROOT_PATH}>View all runtime flags</Link>
        </Button>
      </CardContent>
    </Card>
  </div>
);

export function RuntimeFlagsPage() {
  const { groupSlug } = useParams<{ groupSlug?: string }>();
  const routeGroup = groupSlug ? getRuntimeFlagGroupBySlug(groupSlug) : null;
  const isUnknownGroup = Boolean(groupSlug && !routeGroup);
  const [scope, setScope] = useState<RuntimeFlagScopeMode>("global");
  const [brandId, setBrandId] = useState("");
  const brandsQuery = trpc.admin.brands.list.useQuery({}, { enabled: !isUnknownGroup });
  const firstBrandId = brandsQuery.data?.[0]?.id;

  useEffect(() => {
    if (scope === "brand" && !brandId && firstBrandId) {
      setBrandId(firstBrandId);
    }
  }, [brandId, firstBrandId, scope]);

  const target = buildRuntimeFlagTarget(scope, brandId);
  const hasTarget = target.scope === "global" || target.scopeId.length > 0;
  const flagsQuery = trpc.admin.runtimeFlags.list.useQuery(
    { target },
    { enabled: hasTarget && !isUnknownGroup },
  );

  if (isUnknownGroup) {
    return <UnknownRuntimeFlagGroup />;
  }

  if (flagsQuery.isPending || brandsQuery.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (flagsQuery.error) {
    return <div className="text-sm text-destructive">{flagsQuery.error.message}</div>;
  }

  if (brandsQuery.error) {
    return <div className="text-sm text-destructive">{brandsQuery.error.message}</div>;
  }

  const brands = brandsQuery.data ?? [];
  const flags = flagsQuery.data ?? [];
  const flagGroups = groupRuntimeFlags(flags);
  const groupSelection = getRuntimeFlagGroupsForSlug(flagGroups, groupSlug);
  const visibleFlagGroups = groupSelection.groups;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {groupSelection.selectedGroup?.title ?? "Runtime Flags"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {groupSelection.selectedGroup
            ? groupSelection.selectedGroup.description
            : "Update live operational switches stored in Postgres. Resetting a flag returns it to its inherited value for the selected scope."}
        </p>
      </div>

      <RuntimeFlagScopeSelector
        brands={brands}
        brandId={brandId}
        scope={scope}
        onBrandChange={setBrandId}
        onScopeChange={setScope}
      />

      {scope === "brand" && !brandId ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Select a brand before editing brand-scoped runtime flags.
          </CardContent>
        </Card>
      ) : null}

      {hasTarget && visibleFlagGroups.length > 0 ? (
        <div className="space-y-6">
          {visibleFlagGroups.map((group) => (
            <RuntimeFlagGroupSection key={group.id} group={group} target={target} />
          ))}
        </div>
      ) : hasTarget ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {groupSelection.selectedGroup
              ? "No runtime flags are registered in this category."
              : "No runtime flags are registered."}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
